<template>
  <vue-form @submit="submit"
            :disabled="isFormDisabled"
            :form="form"
            :action="action"
            :status="status">
    <section class="form-section">
      <header>
        <h3>Client</h3>
      </header>
      <div>
        <form-group class="md:w-2/3" field="customer" label="Pour quel client est la récurrence ?">
          <select-customer :config="selectCustomerConfig" v-model="form.customer"/>
        </form-group>
      </div>
    </section>
    <section class="form-section">
      <header>
        <h3>Produits</h3>
        <p>Les produits associés à cette récurrence.</p>
      </header>
      <div>
        <div v-for="(product, index) in form.products" :key="index" class="w-full flex flex-wrap">
          <div class="flex justify-end w-full mt-2 md:mx-2 -mb-6" v-if="index !== 0">
            <vue-button :disabled="isFormDisabled"
                        class="btn btn-xs btn-primary btn-outline mt-2"
                        @click="removeProduct(index)">Supprimer
            </vue-button>
          </div>
          <form-group class="md:w-1/2" :field="`products.${index}.id`" :label="`Produit ${index + 1}`">
            <select-product :config="selectProductConfig" :value="form.products[index]"
                            @input="setProduct($event, index)"/>
          </form-group>
          <form-group class="md:w-1/2" :field="`products.${index}.quantity`" label="Quantité">
            <vue-input-number :disabled="isFormDisabled" :min="1" v-model="form.products[index].quantity"/>
          </form-group>
          <form-group :field="`products.${index}.note`" label="Note">
            <vue-input :disabled="isFormDisabled" v-model="form.products[index].note"/>
          </form-group>
        </div>
        <div class="flex justify-end w-full md:mx-2 mt-4">
          <vue-button :disabled="isFormDisabled"
                      @click="addProduct"
                      class="btn btn-green btn-xs">Ajouter un produit
          </vue-button>
        </div>
      </div>
    </section>
    <section class="form-section">
      <header>
        <h3>Retraits</h3>
        <p>Une commande sera générée pour chaque jour sélectionné.</p>
      </header>
      <div>
        <form-group class="md:w-1/2" field="shop" label="Pour quelle boutique est la commande ?" v-if="shops.length">
          <select-shop :config="selectShopConfig"
                       v-model="form.shop"/>
        </form-group>
        <form-group field="pickups" label="Jours de retrait">
          <weekdays-picker :disabled="isFormDisabled" v-model="weekdays" @input="form.errors.clear()"/>
        </form-group>
        <form-group class="md:w-1/2"
                    v-for="(pickup, index) in pickups"
                    :field="`pickups.${index}.time`"
                    :key="index"
                    :label="pickupLabel(pickup.day)">
          <vue-time-picker v-model="form.pickups[index].time"
                           open-direction="top"
                           start="06:00"
                           end="21:00"
                           :disabled="isFormDisabled"/>
        </form-group>
      </div>
    </section>
    <section class="form-section">
      <header>
        <h3>Périodes</h3>
        <p>
          Périodes pendant lesquelles la commande sera automatiquement générée.
        </p>
      </header>
      <div>
        <div v-for="(period, index) in form.periods" :key="index" class="w-full flex flex-wrap">
          <div class="flex justify-end w-full mt-2 md:mx-2 -mb-6" v-if="index !== 0">
            <vue-button :disabled="isFormDisabled"
                        class="btn btn-xs btn-primary btn-outline mt-2"
                        @click="removePeriod(index)">Supprimer
            </vue-button>
          </div>
          <form-group class="md:w-1/2" :field="`periods.${index}.start`" label="A partir du">
            <vue-date-picker :disabled="isFormDisabled" v-model="form.periods[index].start"/>
          </form-group>
          <form-group class="md:w-1/2" :field="`periods.${index}.end`" label="Jusqu'au">
            <vue-date-picker :disabled="isFormDisabled" v-model="form.periods[index].end" placeholder="Indéfiniment"/>
          </form-group>
        </div>
        <div class="flex justify-end w-full md:mx-2 mt-4">
          <vue-button :disabled="isFormDisabled"
                      @click="addPeriod"
                      class="btn btn-green btn-xs">Ajouter une période
          </vue-button>
        </div>
      </div>
    </section>
    <section class="form-section">
      <header>
        <h3>Statut</h3>
        <p>
          Une récurrence inactive ne sera plus générée.
        </p>
      </header>
      <div>
        <form-group field="active" label="Quel est le statut de la récurrence ?">
          <p class="-m-1">
            <vue-radio class="m-1" :disabled="isFormDisabled" :label="true" v-model="form.active">Active</vue-radio>
            <vue-radio class="m-1" :disabled="isFormDisabled" :label="false" v-model="form.active">Inactive</vue-radio>
          </p>
        </form-group>
      </div>
    </section>
    <section class="form-section" v-if="isUpdateForm && canDelete">
      <header>
        <h3>Zone de danger</h3>
        <p>
          La récurrence sera <strong>définitivement supprimée</strong> et ne pourra pas être restaurée.
        </p>
      </header>
      <div>
        <form-group label="Voulez-vous supprimer définitivement cette récurrence ?">
          <confirm-button @confirmed="destroy"
                          popover-type="popover-top-left sm:popover-right"
                          btn-class="btn-primary btn-outline"
                          btn-confirm-text="Supprimer"
                          :disabled="isFormDisabled"
                          :status="statusDestroy">Supprimer la récurrence
          </confirm-button>
        </form-group>
      </div>
    </section>
  </vue-form>
</template>

<script>
/* globals moment */

import Form from 'form-backend-validation';
import FormGroup from '../FormGroup.vue';
import SelectCustomer from '../customers/CustomerSelect.vue';
import SelectProduct from '../products/ProductSelect.vue';
import SelectShop from '../shops/ShopSelect.vue';
import WeekdaysPicker from '../WeekdaysPicker.vue';
import ConfirmButton from '../ConfirmButton.vue';
import form from '../../mixins/form';
import {formatWeekday} from '../../core/helpers';

export default {
  components: {
    FormGroup,
    SelectCustomer,
    SelectProduct,
    SelectShop,
    WeekdaysPicker,
    ConfirmButton,
  },

  mixins: [form],

  data() {
    return {
      shops: [],
      form: new Form({
        periods: [{start: moment().format('YYYY-MM-DD'), end: null}],
        pickups: [],
        customer: null,
        shop: null,
        active: true,
        products: [{quantity: 1, note: null}],
      }),
    };
  },

  computed: {
    weekdays: {
      get() {
        return this.form.pickups.map(pickup => Number(pickup.day));
      },
      set(weekdays) {
        const current = this.form.pickups.map(pickup => Number(pickup.day));

        // Remove all the pickups not present in the given weekdays
        this.form.pickups.forEach((pickup, index) => {
          if (!weekdays.includes(Number(pickup.day))) {
            this.form.pickups.splice(index, 1);
          }
        });

        // Add the a new pickup for each given weekdays if not already present
        weekdays
            .filter(day => !current.includes(day))
            .forEach(day => this.form.pickups.push({day, time: null}));
      },
    },

    pickups() {
      return this.form.pickups.sort((current, next) => current.day - next.day);
    },

    selectCustomerConfig() {
      return {
        disabled: this.isUpdateForm || this.form.processing,
      };
    },

    selectShopConfig() {
      return {
        disabled: this.isFormDisabled,
      };
    },

    selectProductConfig() {
      return {
        disabled: this.isFormDisabled,
      };
    },
  },

  methods: {
    pickupLabel(weekday) {
      return `Heure de retrait du ${formatWeekday(weekday)}`;
    },

    addPeriod() {
      this.form.periods.push({start: null, end: null});
    },

    removePeriod(index) {
      this.form.periods.splice(index, 1);
    },

    addProduct() {
      this.form.products.push({quantity: 1, note: null});
    },

    removeProduct(index) {
      this.form.products.splice(index, 1);
    },

    setProduct(value, index) {
      const product = {...this.form.products[index]};
      this.$set(this.form.products, index, value);
      this.$set(this.form.products[index], 'quantity', product.quantity);
      this.$set(this.form.products[index], 'note', product.note);
    },
  },

  created() {
    axios
        .get(route('api.shops.index'))
        .then(({data}) => {
          this.shops = data.data;
        })
        .catch(() => {
          this.shops = [];
        });

    if (this.isCreateForm) {
      this.form.shop = window.Pistor.user.shop;
    }
  }
};
</script>
